import Input from '@/components/Input';
import KeyHandler from '@/components/KeyHandler.jsx';
import ModalV2 from '@/components/ModalV2.jsx';
import Stack from '@/components/v3/Stack.jsx';
import useBoundForm from '@/hooks/useBoundForm';
import useUser from '@/hooks/useUser.js';
import sendToast from '@/lib/sendToast.js';
import { postAppRequest } from '@/redux/actions/gravityActions.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors.js';
import { modal } from '@/redux/selectors/uiSelectors';
import workflowScreenSelectors from '@/screens/workflows/redux/selectors/workflowScreenSelectors.js';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const getInitialState = () => ({ name: '', description: '' });

const requestModalCopy = {
  app: {
    title: 'Request an App',
    description: 'Apps are the services and platforms you connect to (like Salesforce, Google Sheets, Slack, etc.)',
    namePlaceholder: 'Which app or service do you need?',
    descriptionPlaceholder: 'What do you need to do with this app?'
  },
  action: {
    title: 'Request an Action',
    description: 'Actions are specific tasks you can perform within an app. Let us know what functionality you need.',
    namePlaceholder: 'What do you need to do?',
    descriptionPlaceholder: 'Describe what functionality you need from the app'
  }
};

const RequestModal = () => {
  const showRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const modalState = useSelector(memoizeSelector('modal:request', (state) => modal(state, 'request')));
  const currentCompany = useSelector(selectors.userSelectedCompany);
  const appSlug = useSelector((state) => workflowScreenSelectors.activeWorkflowStep(state)?.appSlug);
  const user = useUser();
  const form = useBoundForm({ initialState: getInitialState() });

  const companyName = currentCompany?.isPersonal ? 'Personal Account' : currentCompany?.name;
  const userName = user?.jwt?.name;
  const userEmail = user?.jwt?.email;
  const show = modalState?.open;
  const type = modalState?.type;

  const handleClose = () => {
    setModalState('request', { open: false });
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    form.validate();
    if (!form.isValid) {
      setIsSaving(false);
      return;
    }

    try {
      await postAppRequest({ name: form.state.name, description: form.state.description, companyName, userName, userEmail, type, appSlug });
      sendToast({ message: 'Your app request has been submitted! We will get back to you soon.', type: 'success' });
      form.reset(getInitialState());
      handleClose();
    } catch (error) {
      sendToast({ message: 'Failed to submit app request', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const keyMap = { Enter: handleSubmit };
  const actions = [{ children: 'Submit', onClick: handleSubmit, className: 'primary', disabled: !form.state.name, loading: isSaving }];

  useEffect(() => {
    if (show && showRef.current !== show) {
      form.reset(getInitialState());
    }
    showRef.current = show;
  }, [show]);

  return (
    <ModalV2 //
      maxWidth={'500px'} //
      className={'request-app-modal'}
      show={show}
      onHide={handleClose}
      title={requestModalCopy?.[type]?.title}
      description={requestModalCopy?.[type]?.description}
      actions={actions}
      portal>
      <Stack direction={'vertical'} gap={8}>
        <Input id={'name'} label={'Name'} placeholder={requestModalCopy?.[type]?.namePlaceholder} value={form.state.name} onChange={(e) => form.set('name', e.target.value)} required={true} autoFocus={true} />
        <Input id={'description'} type={'textarea'} label={'Description'} placeholder={requestModalCopy?.[type]?.descriptionPlaceholder} value={form.state.description} onChange={(e) => form.set('description', e.target.value)} rows={4} autoSize={true} />
      </Stack>
      <KeyHandler keyMap={keyMap} global={true} preventDefault={true} stopPropagation={true} />
    </ModalV2>
  );
};

export default RequestModal;
